import {
    createPaymentOrder,
    completePaymentOrder,
    doSubscriptionPayment,
    confirmSubscriptionPayment,
    confirmPaymentIntent,
    borrow,
    doBindCardPayment
} from "@/api/logined";
import {
    getClient3DSConfirmPayload,
    syncClientNextAction
} from "@/services/clientNextAction";
import { borrowWithStationBusyRetry } from "@/services/borrowRetry";

export default {
    data() {
        return {
            clientSecret: undefined,
            rentPaymentId: undefined,
            internalId: undefined,
            subscriptionPaymentId: undefined,
            borrowStationBusyWaiting: false,
        };
    },
    methods: {
        ensureAutoQuickLoginAgreement() {
            if (this.autoQuickLogin && !this.agreePolicy) {
                if (typeof this.triggerShake === "function") {
                    this.triggerShake();
                }
                this.toast(this.replaceTranslateContent(this.$t("tMsg.agreePolicy")));
                return false;
            }
            return true;
        },
        handleGuestAccessError(error) {
            const code = Number(error && error.code);
            if (code === 50113) {
                this.isShowGuestPopup = true;
                return true;
            }
            if (code === 50114) {
                this.toast(this.$t(`eMsg.${code}`));
                return true;
            }
            return false;
        },
        toastPaymentError(error) {
            if (this.handleGuestAccessError(error)) {
                return;
            }
            this.toast(this.$t("tMsg.paymentFailed") + ": " + error.message, {
                persistent: !!error?.message,
            });
        },
        getBorrowSubscriptionId() {
            if (this.selectedSubscription?.id) {
                return undefined;
            }
            if (
                (this.ownedSubscriptionRequiresPayment ||
                    this.canUnlockWithOwnedSubscriptionDirectly) &&
                this.authorizationSubscription?.id
            ) {
                return this.authorizationSubscription.id;
            }
            return undefined;
        },
        async createPaymentOrder(paymentMethod, gatewayId, code) {
            try {
                const res = await createPaymentOrder({
                    pay_method_type: paymentMethod,
                    gateway_id: gatewayId,
                    code: code,
                });
                syncClientNextAction(this, res);
                this.rentPaymentId = res.data?.rent_payment_id;
                return res;
            } catch (error) {
                throw error;
            }
        },
        async stripeConfirmPayment(paymentMethodObj, completeCallback = null) {
            try {
                const confirmParams = paymentMethodObj
                    ? { payment_method: paymentMethodObj }
                    : undefined;
                console.log("[3DS][payment_select] stripe.confirmCardPayment request", {
                    clientSecret: this.clientSecret,
                    paymentMethod: paymentMethodObj,
                    confirmParams,
                });
                const res = confirmParams
                    ? await this.stripe.confirmCardPayment(
                        this.clientSecret,
                        confirmParams
                    )
                    : await this.stripe.confirmCardPayment(this.clientSecret);
                console.log("[3DS][payment_select] stripe.confirmCardPayment response", res);
                if (res.error) {
                    completeCallback && completeCallback("fail");
                    throw res.error;
                } else {
                    completeCallback && completeCallback("success");
                    return res;
                }
            } catch (error) {
                completeCallback && completeCallback("fail");
                throw error;
            }
        },
        async completePaymentOrder({
            rentPaymentId,
            paymentMethod = undefined,
            paymentId = undefined,
            metaData = undefined,
            reuseStatus = undefined,
        }) {
            try {
                const res = await completePaymentOrder({
                    rent_payment_id: rentPaymentId,
                    payment_method: paymentMethod,
                    payment_id: paymentId,
                    meta_data: metaData,
                    reuse_status: reuseStatus,
                });
                return res;
            } catch (error) {
                throw error;
            }
        },
        async borrow({
            code,
            cardId,
            verifyType,
            verifyOrderId,
            subscriptionPaymentId,
            subscriptionId,
        }) {
            try {
                const payload = {
                    code: code,
                    card_id: cardId,
                    verify_type: verifyType,
                    verify_order_id: verifyOrderId,
                    subscription_payment_id: subscriptionPaymentId,
                    subscription_id: subscriptionId,
                };
                const res = await borrowWithStationBusyRetry(
                    () => borrow(payload),
                    {
                        onStationBusyChange: (waiting) => {
                            this.borrowStationBusyWaiting = waiting;
                        },
                    }
                );
                return res;
            } catch (error) {
                throw error;
            }
        },
        async doSubscriptionPayment({
            subscriptionId,
            paymentMethod,
            gatewayId = 0,
            cardId = 0,
            userCouponId = undefined,
            outletsId = undefined,
            autoRenew = undefined,
        }) {
            try {
                const res = await doSubscriptionPayment({
                    subscription_id: subscriptionId,
                    pay_method_type: paymentMethod,
                    gateway_id: gatewayId,
                    card_id: cardId,
                    user_coupon_id: userCouponId,
                    outlets_id: outletsId,
                    auto_renew: autoRenew,
                    is_from_webapp: 1,
                });
                syncClientNextAction(this, res);
                this.subscriptionPaymentId = res.data?.subscription_payment_id;
                return res;
            } catch (error) {
                throw error;
            }
        },
        async confirmSubscriptionPayment({
            internalId = undefined,
            paymentId = undefined,
            requestFrom,
            autoRenew,
            reuseStatus = undefined,
        }) {
            try {
                const res = await confirmSubscriptionPayment({
                    internal_id: internalId,
                    payment_id: paymentId,
                    request_from: requestFrom,
                    auto_renew: autoRenew,
                    reuse_status: reuseStatus,
                });
                return res;
            } catch (error) {
                throw error;
            }
        },
        async confirmPaymentIntent({
            gatewayId,
            internalId,
            paymentMethod,
            paymentId,
            reuseStatus = undefined,
        }) {
            try {
                const res = await confirmPaymentIntent({
                    gateway_id: gatewayId,
                    internal_id: internalId,
                    payment_method: paymentMethod,
                    payment_id: paymentId,
                    reuse_status: reuseStatus,
                });
                return res;
            } catch (error) {
                throw error;
            }
        },
        async doBindCardPayment({ paymentMethod, gatewayId }) {
            try {
                const res = await doBindCardPayment({
                    pay_method_type: paymentMethod,
                    gateway_id: gatewayId,
                });
                return res;
            } catch (error) {
                throw error;
            }
        },
        async _handleSubscriptionPayment(paymentMethod, gatewayId, reuseStatus = undefined) {
            const autoRenew = Number(this.selectedSubscription?.auto_renew ?? 0);
            const subRes = await this.doSubscriptionPayment({
                subscriptionId: this.selectedSubscription.id,
                paymentMethod: paymentMethod,
                gatewayId: gatewayId,
                outletsId: this.$store.getters["appConfig/outlet_id"],
                autoRenew,
            });

            const internalId = this.internalId;
            const subscriptionPaymentId = subRes.data?.subscription_payment_id;

            return { internalId, subscriptionPaymentId };
        },

        async _handleRentPayment(paymentMethod, gatewayId, code, reuseStatus = undefined) {
            await this.createPaymentOrder(paymentMethod, gatewayId, code);
            // clientSecret and rentPaymentId are set in createPaymentOrder
            return { rentPaymentId: this.rentPaymentId };
        },
        async refreshAfterSubscriptionPayment() {
            if (typeof this.resetFortisCard === "function") {
                this.resetFortisCard();
            }
            this.selectedMethod = undefined;
            this.selectedCardId = undefined;
            await Promise.all([
                this.getUserWallet(),
                this.getPaymentConfig(),
                this.getSubscriptions(),
                this.getBillingRule(),
            ]);
            this.toast(this.$t("tMsg.subscriptionSuccess"));
        },

        async stripeThirdPartyPayment(ev, method) {
            this.hideLoading();
            try {
                this.isShowPreparing = true;
                const paymentMethodId = ev.paymentMethod.id;

                if (this.selectedSubscription?.id) {
                    const autoRenew = Number(this.selectedSubscription?.auto_renew ?? 0);
                    const { internalId, subscriptionPaymentId } = await this._handleSubscriptionPayment(
                        method === "apple_pay" ? "apple_pay" : "google_pay",
                        this.stripeGatewayId
                    );

                    await this.stripeConfirmPayment(
                        paymentMethodId,
                        (status) => ev.complete(status)
                    );

                    await this.confirmSubscriptionPayment({
                        internalId: internalId,
                        requestFrom: 1,
                        paymentMethod: paymentMethodId,
                        autoRenew,
                        reuseStatus: method === "apple_pay" ? 1 : undefined,
                    });

                    await this.refreshAfterSubscriptionPayment();
                } else {
                    const subscriptionId = this.getBorrowSubscriptionId();
                    await this._handleRentPayment(method, this.stripeGatewayId, this.deviceCode);

                    await this.stripeConfirmPayment(
                        paymentMethodId,
                        (status) => ev.complete(status)
                    );

                    const completeOrderRes = await this.completePaymentOrder({
                        rentPaymentId: this.rentPaymentId,
                        paymentMethod: paymentMethodId,
                        reuseStatus: typeof method === "string" && method.includes("apple") ? 1 : undefined,
                    });

                    await this.borrow({
                        code: this.deviceCode,
                        cardId: completeOrderRes.data.method_id,
                        verifyType: completeOrderRes.data.verify_type,
                        verifyOrderId: completeOrderRes.data.verify_order_id,
                        subscriptionId,
                    });
                    this.$router.push({ name: "borrow_success" });
                }
            } catch (error) {
                ev.complete("fail");
                this.toastPaymentError(error);
            } finally {
                this.isShowPreparing = false;
            }
        },
        async fortisThirdPartyPayment({
            paymentToken,
            internalId,
            rentPaymentId,
            subscriptionPaymentId,
        }) {
            this.hideLoading();
            try {
                this.isShowPreparing = true;

                // Common confirm logic
                await this.confirmPaymentIntent({
                    gatewayId: this.fortisGatewayId,
                    internalId: internalId,
                    paymentMethod: paymentToken,
                    reuseStatus: 0,
                });

                if (this.selectedSubscription?.id) {
                    const autoRenew = Number(this.selectedSubscription?.auto_renew ?? 0);
                    await this.confirmSubscriptionPayment({
                        internalId: internalId,
                        requestFrom: 1,
                        paymentMethod: paymentToken,
                        autoRenew,
                        reuseStatus: 0,
                    });
                    await this.refreshAfterSubscriptionPayment();
                } else {
                    const subscriptionId = this.getBorrowSubscriptionId();
                    const completeOrderRes = await this.completePaymentOrder({
                        rentPaymentId: rentPaymentId,
                        reuseStatus: 0,
                    });
                    await this.borrow({
                        code: this.deviceCode,
                        cardId: completeOrderRes.data.method_id,
                        verifyType: completeOrderRes.data.verify_type,
                        verifyOrderId: completeOrderRes.data.verify_order_id,
                        subscriptionId,
                    });
                    this.$router.push({ name: "borrow_success" });
                }
            } catch (error) {
                this.toastPaymentError(error);
            } finally {
                this.isShowPreparing = false;
            }
        },
        async submitCardPayment() {
            const useGlobalLoading = !!this.hasThawani
            if (!this.ensureAutoQuickLoginAgreement()) {
                return;
            }
            try {
                if (useGlobalLoading) {
                    this.showLoading()
                } else {
                    this.isShowPreparing = true;
                }

                if (this.selectedSubscription?.id) {
                    const autoRenew = Number(this.selectedSubscription?.auto_renew ?? 0);
                    const subscriptionRes = await this.doSubscriptionPayment({
                        subscriptionId: this.selectedSubscription.id,
                        paymentMethod: "web_card",
                        cardId: this.selectedCardId,
                        outletsId: this.outletId,
                        autoRenew,
                    });

                    const client3DSConfirmPayload =
                        getClient3DSConfirmPayload(subscriptionRes);
                    if (client3DSConfirmPayload) {
                        console.log("[3DS][payment_select] subscription requires 3DS", {
                            subscriptionPaymentId:
                                subscriptionRes?.data?.subscription_payment_id,
                            payload: client3DSConfirmPayload,
                        });
                        syncClientNextAction(this, subscriptionRes);
                        await this.stripeConfirmPayment(
                            client3DSConfirmPayload.paymentMethod
                        );
                    }

                    await this.confirmSubscriptionPayment({
                        internalId: this.internalId,
                        requestFrom: 1,
                        autoRenew,
                    });
                    await this.refreshAfterSubscriptionPayment();
                } else {
                    const subscriptionId = this.getBorrowSubscriptionId();
                    const borrowRes = await this.borrow({
                        code: this.deviceCode,
                        cardId: this.selectedCardId,
                        subscriptionId,
                    });
                    const client3DSConfirmPayload =
                        getClient3DSConfirmPayload(borrowRes);
                    if (client3DSConfirmPayload) {
                        console.log("[3DS][payment_select] borrow requires 3DS", {
                            rentPaymentId: borrowRes?.data?.rent_payment_id,
                            verifyType: borrowRes?.data?.verify_type,
                            verifyOrderId: borrowRes?.data?.verify_order_id,
                            payload: client3DSConfirmPayload,
                        });
                        syncClientNextAction(this, borrowRes);
                        await this.stripeConfirmPayment(
                            client3DSConfirmPayload.paymentMethod
                        );

                        await this.borrow({
                            code: this.deviceCode,
                            cardId: this.selectedCardId,
                            subscriptionId,
                        });
                    }
                    this.$router.push({ name: "borrow_success" });
                }
            } catch (error) {
                this.toastPaymentError(error);
            } finally {
                if (useGlobalLoading) {
                    this.hideLoading()
                } else {
                    this.isShowPreparing = false;
                }
            }
        },
        async submitReleaseDirectly() {
            try {
                this.isShowPreparing = true;
                await this.borrow({
                    code: this.deviceCode,
                    subscriptionId: this.getBorrowSubscriptionId(),
                });
                this.$router.push({ name: "borrow_success" });
            } catch (error) {
                this.toastPaymentError(error);
            } finally {
                this.isShowPreparing = false;
            }
        },
    }
}
